import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Activity, TrendingUp, TrendingDown, Zap, Users,
  AlertTriangle, Clock, BarChart3, Gauge, Settings, CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];

const BandwidthManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showThrottleModal, setShowThrottleModal] = useState(false);
  const [showFupModal, setShowFupModal] = useState(false);
  const [throttleReason, setThrottleReason] = useState('');
  const [fupLimit, setFupLimit] = useState(500);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/bandwidth/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        toast.error('Failed to load bandwidth data');
      }
    } catch (error) {
      console.error('Error fetching bandwidth data:', error);
      toast.error('Error loading bandwidth data');
    } finally {
      setLoading(false);
    }
  };

  const generateSampleData = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/bandwidth/generate-sample-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Sample data generated successfully!');
        fetchDashboardData();
      } else {
        toast.error('Failed to generate sample data');
      }
    } catch (error) {
      toast.error('Error generating sample data');
    }
  };

  const applyThrottling = async () => {
    if (!selectedCustomer || !throttleReason.trim()) {
      toast.error('Please provide a reason for throttling');
      return;
    }

    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(
        `${API_URL}/api/bandwidth/throttle/${selectedCustomer.customer_id}?reason=${encodeURIComponent(throttleReason)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Throttling applied successfully');
        setShowThrottleModal(false);
        setThrottleReason('');
        fetchDashboardData();
      } else {
        toast.error('Failed to apply throttling');
      }
    } catch (error) {
      toast.error('Error applying throttling');
    }
  };

  const removeThrottling = async (customerId) => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(
        `${API_URL}/api/bandwidth/unthrottle/${customerId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Throttling removed successfully');
        fetchDashboardData();
      } else {
        toast.error('Failed to remove throttling');
      }
    } catch (error) {
      toast.error('Error removing throttling');
    }
  };

  const updateFupPolicy = async () => {
    if (!selectedCustomer || fupLimit < 1) {
      toast.error('Please enter a valid FUP limit');
      return;
    }

    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(
        `${API_URL}/api/bandwidth/fup/${selectedCustomer.customer_id}?monthly_limit_gb=${fupLimit}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success(`FUP limit updated to ${fupLimit}GB`);
        setShowFupModal(false);
        fetchDashboardData();
      } else {
        toast.error('Failed to update FUP policy');
      }
    } catch (error) {
      toast.error('Error updating FUP policy');
    }
  };

  const formatBytes = (mb) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Loading bandwidth management...</p>
        </div>
      </div>
    );
  }

  const { summary, customers, top_consumers, peak_analysis } = dashboardData || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      {/* Header */}
      <div className="mb-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white p-4 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/monitoring-dashboard')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            {/* Netix Logo */}
            <div className="bg-white rounded-lg p-2 shadow-md">
              <img 
                src="/netix-logo-horizontal.png" 
                alt="Netix Fibernet" 
                className="h-8 w-auto"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Gauge className="h-6 w-6" />
                Bandwidth Management Dashboard
              </h1>
              <p className="text-cyan-100 text-sm mt-1">
                Monitor bandwidth, enforce FUP, and optimize network resources
              </p>
            </div>
          </div>
          <Button
            onClick={generateSampleData}
            className="bg-white text-cyan-600 hover:bg-cyan-50"
          >
            <Zap className="h-4 w-4 mr-2" />
            Generate Sample Data
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">Total Usage (24h)</div>
                <div className="text-3xl font-bold">{summary?.total_usage_24h_gb || 0} GB</div>
              </div>
              <Activity className="h-10 w-10 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">Active Customers</div>
                <div className="text-3xl font-bold">{summary?.total_customers || 0}</div>
              </div>
              <Users className="h-10 w-10 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-pink-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">Throttled</div>
                <div className="text-3xl font-bold">{summary?.throttled_customers || 0}</div>
              </div>
              <TrendingDown className="h-10 w-10 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-yellow-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">FUP Exceeded</div>
                <div className="text-3xl font-bold">{summary?.fup_exceeded || 0}</div>
              </div>
              <AlertTriangle className="h-10 w-10 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - 2 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Top Consumers */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-red-600" />
              Top Bandwidth Consumers (24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_consumers && top_consumers.length > 0 ? (
              <div className="space-y-3">
                {top_consumers.map((consumer, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </div>
                        <span className="font-semibold text-sm">{consumer.customer_name}</span>
                      </div>
                      <span className="text-red-600 font-bold text-sm">
                        {formatBytes(consumer.total_usage_mb)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">
                      <div>⬆️ Upload: {formatBytes(consumer.upload_mb)}</div>
                      <div>⬇️ Download: {formatBytes(consumer.download_mb)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-8">
                <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No bandwidth data available</p>
                <p className="text-xs mt-1">Generate sample data to see statistics</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Peak Usage Analysis */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              Peak Usage Time Analysis (7 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {peak_analysis && peak_analysis.hourly_analysis && peak_analysis.hourly_analysis.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={peak_analysis.hourly_analysis}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" label={{ value: 'Hour of Day', position: 'insideBottom', offset: -5 }} />
                  <YAxis label={{ value: 'Avg Usage (MB)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Bar dataKey="avg_usage_mb" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-gray-400 py-8 h-[250px] flex flex-col items-center justify-center">
                <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No peak usage data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Customer Bandwidth Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" />
            Customer Bandwidth Usage & FUP Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {customers && customers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">24h Usage</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Monthly FUP</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {customers.map((customer, idx) => {
                    const bandwidth = customer.bandwidth || {};
                    const last24h = bandwidth.last_24h || {};
                    const fup = bandwidth.fup || {};
                    const fupPercentage = fup.monthly_limit_gb 
                      ? ((fup.current_usage_mb / 1024) / fup.monthly_limit_gb) * 100 
                      : 0;
                    
                    return (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-sm">{customer.customer_name}</div>
                          <div className="text-xs text-gray-500">{customer.static_ip}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-semibold">{formatBytes(last24h.total_mb || 0)}</div>
                          <div className="text-xs text-gray-500">
                            ⬆️{formatBytes(last24h.upload_mb || 0)} ⬇️{formatBytes(last24h.download_mb || 0)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {fup.monthly_limit_gb ? (
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full ${
                                      fupPercentage >= 100 ? 'bg-red-500' :
                                      fupPercentage >= 90 ? 'bg-orange-500' :
                                      fupPercentage >= 80 ? 'bg-yellow-500' : 'bg-green-500'
                                    }`}
                                    style={{ width: `${Math.min(fupPercentage, 100)}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs font-semibold">{fupPercentage.toFixed(0)}%</span>
                              </div>
                              <div className="text-xs text-gray-600">
                                {(fup.current_usage_mb / 1024).toFixed(1)} / {fup.monthly_limit_gb} GB
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">No FUP set</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {bandwidth.throttled ? (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                              Throttled
                            </span>
                          ) : fupPercentage >= 100 ? (
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                              FUP Exceeded
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                              <CheckCircle className="h-3 w-3" />
                              Normal
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {bandwidth.throttled ? (
                              <Button
                                size="sm"
                                onClick={() => removeThrottling(customer.customer_id)}
                                className="bg-green-500 hover:bg-green-600 text-white text-xs"
                              >
                                Unthrottle
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedCustomer(customer);
                                  setShowThrottleModal(true);
                                }}
                                className="bg-red-500 hover:bg-red-600 text-white text-xs"
                              >
                                Throttle
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedCustomer(customer);
                                setFupLimit(fup.monthly_limit_gb || 500);
                                setShowFupModal(true);
                              }}
                              className="bg-blue-500 hover:bg-blue-600 text-white text-xs"
                            >
                              <Settings className="h-3 w-3 mr-1" />
                              FUP
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center text-gray-400 py-8">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No customer data available</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Throttle Modal */}
      {showThrottleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Apply Speed Throttling</h3>
            <p className="text-sm text-gray-600 mb-4">
              Customer: <strong>{selectedCustomer?.customer_name}</strong>
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Throttling
              </label>
              <textarea
                value={throttleReason}
                onChange={(e) => setThrottleReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows="3"
                placeholder="e.g., FUP exceeded, Network abuse, etc."
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => {
                  setShowThrottleModal(false);
                  setThrottleReason('');
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={applyThrottling}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Apply Throttling
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* FUP Modal */}
      {showFupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Update FUP Policy</h3>
            <p className="text-sm text-gray-600 mb-4">
              Customer: <strong>{selectedCustomer?.customer_name}</strong>
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monthly Bandwidth Limit (GB)
              </label>
              <input
                type="number"
                value={fupLimit}
                onChange={(e) => setFupLimit(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="1"
                max="10000"
              />
              <p className="text-xs text-gray-500 mt-1">
                Common limits: 100GB, 250GB, 500GB, 1000GB (1TB), Unlimited (9999GB)
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => setShowFupModal(false)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={updateFupPolicy}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                Update FUP
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BandwidthManagement;
